import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getCategoryById } from "../api/CategoryAPI";
import { getWhyPageData, whyScreenshotUrl } from "../api/ProspectAPI";
import { getAllUnits } from "../api/UnitAPI";
import { Category } from "../types/Category";
import { Unit } from "../types/Unit";
import { InventoryParameter } from "../types/Inventory";
import CreateParametersForm from "./inventory/CreateParametersForm";
import DynamicForm from "./DynamicForm";
import GeoAreaPicker from "./geo/GeoAreaPicker";
import { CurrentListing, WhyPageFrame, useWhyTexts } from "./WhyCategoryPage";

/**
 * The EXPERIMENTAL why page (/why1/...) living beside the proven one
 * (/why/...): form first, explanation second. The prospect lands directly on
 * the create-listing form for their category, then reads why it works — words vs meaning, location, and that
 * searching uses the very same form; their current listing (screenshot or
 * fetched preview) opens the page. Shares the backend (why data, texts, feedback) with v1;
 * promote by pointing the /why routes here, retire by deleting this file.
 */

const geoInitialOf = (d: {
    countryId: number | null; admin1Id: number | null;
    admin2Id: number | null; cityId: number | null;
} | null) => d ? {
    countryId: d.countryId, admin1Id: d.admin1Id, admin2Id: d.admin2Id, cityId: d.cityId,
} : null;

const Why1Body: React.FC<{
    category: Category | null;
    /** Their current listing (screenshot preferred, else the fetched
     *  preview) — shown at the very top when we have anything to show. */
    listing?: CurrentListing | null;
    initialLocation?: {
        countryId?: number | null; admin1Id?: number | null;
        admin2Id?: number | null; cityId?: number | null;
    } | null;
}> = ({ category, listing, initialLocation }) => {
    // Marketing copy from the DB (template editor), with built-in fallbacks.
    const texts = useWhyTexts();
    const name = category?.name ?? "your";
    const isService = !!category?.hasServiceArea;
    const hasItemLocation = !!category?.hasItemLocation;
    const params = category?.parameters ?? [];

    const [units, setUnits] = useState<Unit[]>([]);
    useEffect(() => {
        getAllUnits("").then(setUnits).catch(() => setUnits([]));
    }, []);

    // Demo parameter values live only on this page.
    const [demoValues, setDemoValues] = useState<Record<string, InventoryParameter>>({});
    const onDemoValue = (pname: string, value: InventoryParameter | null) => {
        setDemoValues((v) => {
            const next = { ...v };
            if (value === null) delete next[pname]; else next[pname] = value;
            return next;
        });
    };

    // Short page, short form: first rows + a show-all expander.
    const DEMO_ROWS = 6;
    const [allParams, setAllParams] = useState(false);
    const shownParams = allParams ? params : params.slice(0, DEMO_ROWS);

    // The search-side proof, folded behind the "same form" line.
    const [searchDemoOpen, setSearchDemoOpen] = useState(false);

    const panel: React.CSSProperties = {
        border: "2px solid var(--text-primary, #1e293b)",
        borderRadius: "8px",
        padding: "1rem",
        minWidth: 0,
    };
    const innerBox: React.CSSProperties = {
        border: "1px solid var(--border-color, #eee)",
        borderRadius: "6px",
        padding: "0.6rem",
        marginBottom: "0.6rem",
    };
    const prose: React.CSSProperties = {
        maxWidth: "760px",
        margin: "1.25rem auto",
        textAlign: "left",
        fontSize: "1rem",
        lineHeight: 1.5,
    };

    return (
        <div className="why-panels" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {/* 0 — their current listing or website, when we have it. */}
            {(listing?.screenshotUrl || listing?.imageUrl || listing?.title) && (
                <div style={{ ...panel, background: "var(--bg-secondary, #f8f8f8)" }}>
                    <h3 style={{ marginTop: 0 }}>Your current listing</h3>
                    <div style={{ maxWidth: "640px" }}>
                        {listing?.screenshotUrl ? (
                            <div style={{ border: "1px solid var(--border-color, #ddd)", borderRadius: "6px", padding: "0.5rem", background: "#fff" }}>
                                {listing.url && (
                                    <a href={listing.url} target="_blank" rel="noopener noreferrer"
                                       style={{ display: "inline-block", fontSize: "0.85rem", marginBottom: "0.35rem" }}>
                                        see the full original post ↗
                                    </a>
                                )}
                                <img
                                    src={listing.screenshotUrl}
                                    alt={listing.title ?? "Your current listing"}
                                    style={{ width: "100%", height: "auto", borderRadius: "4px", display: "block" }}
                                />
                            </div>
                        ) : (
                            <div style={{ border: "1px solid var(--border-color, #ddd)", borderRadius: "6px", padding: "0.75rem", background: "#fff" }}>
                                {listing?.url && (
                                    <a href={listing.url} target="_blank" rel="noopener noreferrer"
                                       style={{ display: "inline-block", fontSize: "0.85rem", marginBottom: "0.35rem" }}>
                                        see the full original post ↗
                                    </a>
                                )}
                                {listing?.title && (
                                    <div style={{ fontWeight: 600, fontSize: "1.05rem", marginBottom: "0.5rem" }}>{listing.title}</div>
                                )}
                                {listing?.imageUrl && (
                                    <img
                                        src={listing.imageUrl}
                                        alt={listing.title ?? "Your current listing"}
                                        style={{ width: "100%", height: "auto", borderRadius: "4px", display: "block" }}
                                    />
                                )}
                                {listing?.description && (
                                    <p style={{ whiteSpace: "pre-wrap", fontSize: "0.9rem", color: "#333", margin: "0.5rem 0 0.25rem" }}>
                                        {listing.description}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* The one-breath pitch, right before they touch the form. */}
            <div style={{ ...prose, margin: "0 auto" }}>
                <p style={{ margin: 0 }}>
                    {(() => {
                        // First sentence bold, the rest plain.
                        const t = texts.pitch;
                        const cut = t.indexOf(". ");
                        if (cut < 0) return <strong>{t}</strong>;
                        return <>
                            <strong>{t.slice(0, cut + 1)}</strong> {t.slice(cut + 2)}
                        </>;
                    })()}
                </p>
            </div>

            {/* 1 — the product first: the real create form for their category. */}
            <div style={panel}>
                <h3 style={{ marginTop: 0 }}>
                    {category ? `Your ${name} listing on BabaHanks — try the form` : "Your listing on BabaHanks — try the form"}
                </h3>
                <div style={{ fontSize: "0.9rem", color: "var(--text-secondary)", marginBottom: "0.5rem" }}>
                    This is the category-specific form your listing would use — every
                    detail a real field. Nothing here is saved — play with it.
                </div>

                {category && params.length > 0 && (
                    <div style={{ ...innerBox, marginBottom: 0 }}>
                        <div>
                            <CreateParametersForm
                                hideNote
                                parameters={shownParams}
                                units={units}
                                values={demoValues}
                                onChange={onDemoValue}
                                headerExtra={
                                    <div style={{ fontWeight: 700, fontSize: "0.9rem", margin: "0.1rem 0" }}>
                                        {texts.formNudge}
                                    </div>
                                }
                            />
                            {params.length > DEMO_ROWS && (
                                <button
                                    type="button"
                                    className="btn-sm btn-secondary"
                                    style={{ marginTop: "0.4rem", fontWeight: 700 }}
                                    onClick={() => setAllParams((a) => !a)}
                                >
                                    {allParams
                                        ? "Show fewer fields"
                                        : `… and ${params.length - DEMO_ROWS} more fields — show them all`}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* 2 — place, right under the form: the two rules (the one that
                applies to their category highlighted) and the live picker. */}
            <div style={panel}>
                <h3 style={{ marginTop: 0 }}>To help searchers find you by place</h3>
                <ol style={{ paddingLeft: "1.4rem", margin: "0 0 0.75rem" }}>
                    <li style={isService ? { fontWeight: 700 } : undefined}>
                        {texts.placeService}
                        {isService && (
                            <span style={{ color: "var(--primary-color, #4f46e5)" }}>
                                {" "}← this is how your {name} listing works
                            </span>
                        )}
                    </li>
                    <li style={{ marginTop: "0.4rem", ...(hasItemLocation ? { fontWeight: 700 } : {}) }}>
                        {texts.placeLocation}
                        {hasItemLocation && (
                            <span style={{ color: "var(--primary-color, #4f46e5)" }}>
                                {" "}← this is how your {name} listing works
                            </span>
                        )}
                    </li>
                </ol>
                <div style={{ ...innerBox, marginBottom: 0 }}>
                    <strong style={{ fontSize: "0.9rem" }}>
                        {isService ? "The areas you serve" : "Where this listing is located"}
                    </strong>
                    {isService && (
                        <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)", marginTop: "0.15rem" }}>
                            You can have a combination of cities, counties, states, etc. —
                            anyone searching inside any of them finds you.
                        </div>
                    )}
                    <div style={{ marginTop: "0.4rem" }}>
                        <GeoAreaPicker
                            token=""
                            context={isService ? "provider" : "item"}
                            onChange={() => {}}
                            initial={geoInitialOf(initialLocation ? {
                                countryId: initialLocation.countryId ?? null,
                                admin1Id: initialLocation.admin1Id ?? null,
                                admin2Id: initialLocation.admin2Id ?? null,
                                cityId: initialLocation.cityId ?? null,
                            } : null)}
                        />
                    </div>
                </div>

            </div>

            {/* 3 — the close: searching is the same form (expandable proof). */}
            <div style={{ ...prose, margin: "0.5rem auto" }}>
                <p style={{ margin: 0 }}>
                    <button
                        type="button"
                        onClick={() => setSearchDemoOpen((o) => !o)}
                        style={{
                            background: "none", border: "none", padding: 0, cursor: "pointer",
                            font: "inherit", fontWeight: 700,
                            color: "var(--primary-color, #4f46e5)", textDecoration: "underline",
                        }}
                    >
                        Searching uses the same form {searchDemoOpen ? "▴" : "— see it ▾"}
                    </button>{" "}
                    {texts.sameForm}
                </p>
            </div>

            {searchDemoOpen && category && (
                <div style={panel}>
                    <h3 style={{ marginTop: 0 }}>How people search for {name}</h3>
                    <div style={{ fontSize: "0.9rem", color: "var(--text-secondary)", marginBottom: "0.5rem" }}>
                        The actual search form — same fields you just filled as a lister.
                    </div>
                    <div style={innerBox}>
                        <strong style={{ fontSize: "0.9rem" }}>
                            {isService ? "Where you need the service" : "Where you're looking"}
                        </strong>
                        <div style={{ marginTop: "0.4rem" }}>
                            <GeoAreaPicker
                                token=""
                                context={isService ? "searcher" : "itemSearcher"}
                                onChange={() => {}}
                                initial={geoInitialOf(initialLocation ? {
                                    countryId: initialLocation.countryId ?? null,
                                    admin1Id: initialLocation.admin1Id ?? null,
                                    admin2Id: initialLocation.admin2Id ?? null,
                                    cityId: initialLocation.cityId ?? null,
                                } : null)}
                            />
                        </div>
                    </div>
                    {params.length > 0 && (
                        <div style={{ ...innerBox, marginBottom: 0 }}>
                            <strong style={{ fontSize: "0.9rem" }}>What you're looking for</strong>
                            <div style={{ marginTop: "0.4rem" }}>
                                <DynamicForm
                                    key={category.id}
                                    parameters={shownParams}
                                    categoryId={category.id}
                                    units={units}
                                    onSubmit={() => {}}
                                    formId="why1-search-demo-form"
                                    hideSubmit
                                />
                                {params.length > DEMO_ROWS && (
                                    <button
                                        type="button"
                                        className="btn-sm btn-secondary"
                                        style={{ marginTop: "0.4rem", fontWeight: 700 }}
                                        onClick={() => setAllParams((a) => !a)}
                                    >
                                        {allParams
                                            ? "Show fewer fields"
                                            : `… and ${params.length - DEMO_ROWS} more fields — show them all`}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="why-cta-row" style={{ textAlign: "center", margin: "0.25rem 0" }}>
                <Link
                    to="/register"
                    style={{
                        display: "inline-block",
                        background: "var(--primary-color, #4f46e5)", color: "#fff",
                        padding: "0.6rem 1.4rem", borderRadius: "8px",
                        fontWeight: 700, textDecoration: "none",
                    }}
                >
                    Create your account — listing takes just a few minutes
                </Link>
                <div style={{ fontSize: "0.85rem", color: "var(--text-secondary)", marginTop: "0.3rem" }}>
                    Free — and your current post stays right where it is.
                </div>
            </div>
        </div>
    );
};

/** /why1/p/{token}: the experimental page with the prospect's prefills. */
export const Why1ProspectPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [category, setCategory] = useState<Category | null>(null);
    const [listing, setListing] = useState<CurrentListing | null>(null);
    const [initialLocation, setInitialLocation] = useState<{
        countryId: number | null; admin1Id: number | null;
        admin2Id: number | null; cityId: number | null;
    } | null>(null);
    const [failed, setFailed] = useState(false);
    // Set only while their managed org awaits claiming.
    const [claimToken, setClaimToken] = useState<string | null>(null);
    const [orgName, setOrgName] = useState<string | null>(null);

    useEffect(() => {
        if (!token) { setFailed(true); return; }
        getWhyPageData(token)
            .then((d) => {
                setListing({
                    url: d.listingUrl,
                    title: d.listingTitle,
                    imageUrl: d.listingImageUrl,
                    description: d.listingDescription,
                    screenshotUrl: d.hasScreenshot && token ? whyScreenshotUrl(token) : null,
                });
                setInitialLocation({
                    countryId: d.countryId, admin1Id: d.admin1Id,
                    admin2Id: d.admin2Id, cityId: d.cityId,
                });
                setClaimToken(d.claimToken);
                setOrgName(d.orgName);
                if (d.categoryId) {
                    getCategoryById("", d.categoryId).then(setCategory).catch(() => {});
                }
            })
            .catch(() => setFailed(true));
    }, [token]);

    return (
        <WhyPageFrame
            showGenericLink={failed}
            feedback={{ token }}
        >
            {claimToken && (
                <div className="why-cta-row" style={{
                    border: "2px solid #16a34a", borderRadius: "8px", background: "#f0fdf4",
                    padding: "0.75rem 1rem", margin: "0 0 1rem",
                    display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap",
                }}>
                    <div style={{ minWidth: "16rem", flex: "1 1 20rem" }}>
                        <strong>
                            {orgName ? `${orgName}'s listing page is already live on BabaHanks.` : "Your listing page is already live on BabaHanks."}
                        </strong>{" "}
                        <span style={{ color: "var(--text-secondary)" }}>
                            We set it up from public information — people searching your
                            area can find you right now. It's yours to take over, free.
                        </span>
                    </div>
                    <Link
                        to={`/claim/${claimToken}`}
                        style={{
                            background: "#16a34a", color: "#fff", fontWeight: 700,
                            padding: "0.5rem 1.1rem", borderRadius: "8px", textDecoration: "none",
                            whiteSpace: "nowrap",
                        }}
                    >
                        Take ownership →
                    </Link>
                </div>
            )}
            <Why1Body category={category} listing={listing} initialLocation={initialLocation} />
        </WhyPageFrame>
    );
};

/** /why1/c/{categoryId}: the experimental page, category only. */
export const Why1CategoryPage: React.FC = () => {
    const { categoryId } = useParams<{ categoryId: string }>();
    const [category, setCategory] = useState<Category | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!categoryId) { setFailed(true); return; }
        getCategoryById("", categoryId)
            .then(setCategory)
            .catch(() => setFailed(true));
    }, [categoryId]);

    return (
        <WhyPageFrame
            showGenericLink={failed}
            feedback={{ categoryId }}
        >
            <Why1Body category={category} />
        </WhyPageFrame>
    );
};

export default Why1ProspectPage;